<template>
  <div class="container-fluid">
    <div class="row" id="app">
      <!-- Inputs Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <TopbarNav />
        <hr/>
            <h5>Stringer Properties</h5>
            <!-- Section Area -->
            <div class="row">
              <div class="col-6">
                <label for="A">Section Area, A:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="A"
                  v-model="A"
                  step="0.01"
                /><span>in²</span>
              </div>
            </div>
            <!-- Moment of Inertia Ix -->
            <div class="row">
              <div class="col-6">
                <label for="Ix">Moment of Inertia, Ix:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Ix"
                  v-model="Ix"
                  step="0.01"
                /><span>in⁴</span>
              </div>
            </div>
            <!-- Moment of Inertia Iy -->
            <div class="row">
              <div class="col-6">
                <label for="Iy">Moment of Inertia, Iy:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Iy"
                  v-model="Iy"
                  step="0.01"
                /><span>in⁴</span>
              </div>
            </div>
            <hr/>
            <h5>Loading and Geometry</h5>
            <!-- Weight of Stair -->
            <div class="row">
              <div class="col-6">
                <label for="Ws">Weight of Stair, Ws:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Ws"
                  v-model="Ws"
                  step="1"
                /><span>lb</span>
              </div>
            </div>
            <p class="fst-italic">
              (Including guardrails, treads, stringers and soffit)
            </p>
            <!-- Horizontal Distance -->
            <div class="row">
              <div class="col-6">
                <label for="Lh">Horizontal Distance, Lh:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Lh"
                  v-model="Lh"
                  step="0.1"
                /><span>ft</span>
              </div>
            </div>
            <!-- Vertical Distance -->
            <div class="row">
              <div class="col-6">
                <label for="Lv">Vertical Distance, Lv:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Lv"
                  v-model="Lv"
                  step="0.1"
                /><span>ft</span>
              </div>
            </div>
            <p class="fst-italic">(Distance between supports)</p>
            <hr/>
            <h5>Damping</h5>
            <!-- Damping Ratio -->
            <div class="row">
              <div class="col-6">
                <label for="beta">Damping Ratio, β:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="beta"
                  v-model="beta"
                  step="0.001"
                  min="0"
                  max="1"
                />
              </div>
            </div>
            <p class="fst-italic">(Typical value: 0.01 to 0.03)</p>
            <hr/>
            <h5>Mode Shape Parameters</h5>
            <!-- xw -->
            <div class="row">
              <div class="col-6">
                <label for="xw">Distance to walker, xw:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="xw"
                  v-model="xw"
                  step="0.1"
                  :max="Ls"
                  :disabled="conservativeMode"
                /><span>ft</span>
              </div>
            </div>
            <p class="fst-italic">(From end of stringer to walker excitation force location)</p>
            <!-- xr -->
            <div class="row">
              <div class="col-6">
                <label for="xr">Distance to response, xr:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="xr"
                  v-model="xr"
                  step="0.1"
                  :max="Ls"
                  :disabled="conservativeMode"
                /><span>ft</span>
              </div>
            </div>
            <p class="fst-italic">(From end of stringer to response location)</p>
            <!-- Conservative Mode Checkbox -->
            <div class="row">
              <div class="col-12">
                <input
                  type="checkbox"
                  name="conservativeMode"
                  v-model="conservativeMode"
                  id="conservativeMode"
                />
                <label for="conservativeMode">Conservatively take φ<sub>w</sub> and φ<sub>R</sub> equal to 1</label>
              </div>
            </div>
            <hr/>
            <h5>Descent Parameters</h5>
            <!-- Q -->
            <div class="row">
              <div class="col-6">
                <label for="Q">Assumed Body Weight, Q:</label>
              </div>
              <div class="col-6">
                <input
                  type="number"
                  name="Q"
                  v-model="Q"
                  step="1"
                  min="1"
                /><span>lb</span>
              </div>
            </div>
      </div>
      <!-- Report Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <h4>Stair Vibration Check</h4>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              Based on AISC Design Guide 11
            </p>
          </div>
        </div>
        <hr/>
        
        <!-- Input Summary -->
        <h5>Input Summary:</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'A = ' + parseFloat(A).toFixed(2) + ' \\text{ in}^2'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Section area</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'I_x = ' + parseFloat(Ix).toFixed(0) + ' \\text{ in}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Moment of inertia</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'I_y = ' + parseFloat(Iy).toFixed(0) + ' \\text{ in}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Moment of inertia</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'W_s = ' + parseFloat(Ws).toFixed(0) + ' \\text{ lb}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Weight of stair</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'L_h = ' + parseFloat(Lh).toFixed(1) + ' \\text{ ft}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Horizontal distance</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'L_v = ' + parseFloat(Lv).toFixed(1) + ' \\text{ ft}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Vertical distance</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'L_s = \\sqrt{L_h^2 + L_v^2}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\sqrt{' + parseFloat(Lh).toFixed(1) + '^2 + ' + parseFloat(Lv).toFixed(1) + '^2}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'L_s = ' + Ls.toFixed(1) + ' \\text{ ft}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Diagonal distance between supports</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\theta = \\tan^{-1}\\left(\\frac{L_v}{L_h}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\tan^{-1}\\left(\\frac{' + parseFloat(Lv).toFixed(1) + '}{' + parseFloat(Lh).toFixed(1) + '}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\theta = ' + theta_deg.toFixed(2) + '^\\circ'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Stair angle</p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\beta = ' + parseFloat(beta).toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Damping ratio</p>
          </div>
        </div>
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'x_w = ' + parseFloat(xw).toFixed(1) + ' \\text{ ft}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Distance to walker location</p>
          </div>
        </div>
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'x_R = ' + parseFloat(xr).toFixed(1) + ' \\text{ ft}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Distance to response location</p>
          </div>
        </div>
        <hr/>
        
        <!-- Vertical Natural Frequency -->
        <h5>Vertical Natural Frequency</h5>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'I_v = 2(' + parseFloat(Ix).toFixed(0) + ' \\text{ in.}^4)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= ' + Iv.toFixed(0) + ' \\text{ in.}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'f_v = \\frac{\\pi}{2} \\sqrt{\\frac{gEI_v}{W_s L_s^3}}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-7)</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\frac{\\pi}{2} \\left[\\frac{(386 \\text{ in/s}^2)(29 \\times 10^6 \\text{ psi})(' + Iv.toFixed(0) + ' \\text{ in.}^4)}{(' + parseFloat(Ws).toFixed(0) + ' \\text{ lb})(' + parseFloat(Ls).toFixed(1) + ' \\text{ ft})^3(1,728 \\text{ in.}^3/\\text{ft}^3)}\\right]^{1/2}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= ' + fv.toFixed(2) + ' \\text{ Hz}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              The stair vertical natural frequency is 
              <span :class="verticalCheck ? 'text-success' : 'text-danger'">
                {{ verticalCheck ? 'greater' : 'less' }}
              </span>
              than 5.0 Hz, the design is 
              <span :class="verticalCheck ? 'text-success' : 'text-danger'">
                {{ verticalCheck ? 'satisfactory' : 'not satisfactory' }}
              </span>
              for this check.
            </p>
          </div>
        </div>
        
        <hr/>
        
        <!-- Horizontal Natural Frequency -->
        <h5>Horizontal Natural Frequency</h5>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              Conservatively assuming that stringer minor-axis bending provides all horizontal stiffness:
            </p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'I_h = 2(' + parseFloat(Iy).toFixed(0) + ' \\text{ in.}^4)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= ' + Ih.toFixed(0) + ' \\text{ in.}^4'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'f_h = \\frac{\\pi}{2} \\sqrt{\\frac{gEI_h}{W_s L_s^3}}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-7)</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\frac{\\pi}{2} \\left[\\frac{(386 \\text{ in/s}^2)(29 \\times 10^6 \\text{ psi})(' + Ih.toFixed(0) + ' \\text{ in.}^4)}{(' + parseFloat(Ws).toFixed(0) + ' \\text{ lb})(' + parseFloat(Ls).toFixed(1) + ' \\text{ ft})^3(1,728 \\text{ in.}^3/\\text{ft}^3)}\\right]^{1/2}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'= ' + fh.toFixed(2) + ' \\text{ Hz}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              The stair horizontal natural frequency is 
              <span :class="horizontalCheck ? 'text-success' : 'text-danger'">
                {{ horizontalCheck ? 'greater' : 'less' }}
              </span>
              than 2.5 Hz, the design is 
              <span :class="horizontalCheck ? 'text-success' : 'text-danger'">
                {{ horizontalCheck ? 'satisfactory' : 'not satisfactory' }}
              </span>
              for this check.
            </p>
          </div>
        </div>
        
        <hr/>
        
        <!-- Mode Shape Values -->
        <h5>Mode Shape Values:</h5>
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'\\phi_W = \\sin\\left(\\frac{\\pi x_w}{L_s}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-9)</p>
          </div>
        </div>
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\sin\\left(\\frac{\\pi \\times ' + parseFloat(xw).toFixed(1) + '}{' + parseFloat(Ls).toFixed(1) + '}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\phi_W = ' + phi_w.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic" v-if="conservativeMode">Conservative value</p>
            <p class="fst-italic" v-else>Unity normalized mode shape at walker</p>
          </div>
        </div>
        
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'\\phi_R = \\sin\\left(\\frac{\\pi x_R}{L_s}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-10)</p>
          </div>
        </div>
        <div class="row" v-if="!conservativeMode">
          <div class="col-6">
            <KaTeXComponent :expression="'= \\sin\\left(\\frac{\\pi \\times ' + parseFloat(xr).toFixed(1) + '}{' + parseFloat(Ls).toFixed(1) + '}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic"></p>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\phi_R = ' + phi_r.toFixed(3)" />
          </div>
          <div class="col-6">
            <p class="fst-italic" v-if="conservativeMode">Conservative value</p>
            <p class="fst-italic" v-else>Unity normalized mode shape at response</p>
          </div>
        </div>
        
        <hr/>
        
        <!-- Acceptance Criterion -->
        <h5>Acceptance Criterion:</h5>
        
        <!-- Case 1 -->
        <div class="row">
          <div class="col-12">
            <h6>Case 1: Normal descents</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\frac{a_p}{g} = 0.62e^{-\\gamma f_v} \\frac{RQ\\cos^2\\theta}{\\beta W_s} \\phi_W \\phi_R \\left(1-e^{-100\\beta}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-8)</p>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'= 0.62e^{-0.29 \\times ' + fv.toFixed(2) + '} \\frac{0.7 \\times ' + parseFloat(Q).toFixed(0) + ' \\times \\cos^2(' + theta_deg.toFixed(2) + '^\\circ)}{' + parseFloat(beta).toFixed(3) + ' \\times ' + parseFloat(Ws).toFixed(0) + '} \\times ' + phi_w.toFixed(3) + ' \\times ' + phi_r.toFixed(3) + ' \\times \\left(1-e^{-100 \\times ' + parseFloat(beta).toFixed(3) + '}\\right)'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'\\frac{a_p}{g} = ' + (normal_descent() * 100).toFixed(2) + '\\%'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              <span v-if="normal_descent() * 100 <= 1.7" class="text-success">
                The predicted peak acceleration does not exceed the Table 4-5 tolerance limit, 1.7%g; thus, individuals descending the stair at normal speeds are not expected to cause objectionable vibrations from people standing on the stair.
              </span>
              <span v-else class="text-danger">
                The predicted peak acceleration exceeds the Table 4-5 tolerance limit, 1.7%g. Consequently, this loading is expected to cause objectionable vibrations. The stair is unsatisfactory if this load case is to be considered.
              </span>
            </p>
          </div>
        </div>
        <div style="margin-bottom: 20px;"></div>
        
        <!-- Case 2 -->
        <div class="row">
          <div class="col-12">
            <h6>Case 2: Rapidly descending individual—not perceptible</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\frac{a_p}{g} = 0.62e^{-\\gamma f_v} \\frac{RQ\\cos^2\\theta}{\\beta W_s} \\phi_W \\phi_R \\left(1-e^{-100\\beta}\\right)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">(Eq. 4-8)</p>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'= 0.62e^{-0.19 \\times ' + fv.toFixed(2) + '} \\frac{' + (fv <= 8 ? '0.5' : '0.7') + ' \\times ' + parseFloat(Q).toFixed(0) + ' \\times \\cos^2(' + theta_deg.toFixed(2) + '^\\circ)}{' + parseFloat(beta).toFixed(3) + ' \\times ' + parseFloat(Ws).toFixed(0) + '} \\times ' + phi_w.toFixed(3) + ' \\times ' + phi_r.toFixed(3) + ' \\times \\left(1-e^{-100 \\times ' + parseFloat(beta).toFixed(3) + '}\\right)'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'\\frac{a_p}{g} = ' + (rapid_descent_ind() * 100).toFixed(2) + '\\%'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              <span v-if="rapid_descent_ind() * 100 <= 3.0" class="text-success">
                The predicted peak acceleration does not exceed the Table 4-5 tolerance limit, 3%g; thus, individuals rapidly descending the stair are not expected to cause objectionable vibrations from people standing on the stair.
              </span>
              <span v-else class="text-danger">
                The predicted peak acceleration exceeds the Table 4-5 tolerance limit, 3%g. Consequently, this loading is expected to cause objectionable vibrations. The stair is unsatisfactory if this load case is to be considered.
              </span>
            </p>
          </div>
        </div>
        <div style="margin-bottom: 20px;"></div>
        
        <!-- Case 3 -->
        <div class="row">
          <div class="col-12">
            <h6>Case 3: Rapidly descending individual—perceptible; rapidly descending group</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'a_p = 3(' + (rapid_descent_ind() * 100).toFixed(2) + '\\%g)'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <KaTeXComponent :expression="'= ' + (3 * rapid_descent_ind() * 100).toFixed(2) + '\\%g'" />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic">
              <span v-if="(3 * rapid_descent_ind() * 100) <= 4.5" class="text-success">
                This acceleration does not exceed the Table 4-5 tolerance limit of 4.5%g. Consequently, this loading is not expected to cause strongly perceptible vibrations.
              </span>
              <span v-else class="text-danger">
                This acceleration exceeds the Table 4-5 tolerance limit of 4.5%g. Consequently, this loading is expected to cause strongly perceptible vibrations. The stair is unsatisfactory if this load case is to be considered.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TopbarNav from "@/components/TopbarNav.vue";

export default {
  name: "StairVibration",
  components: {
    TopbarNav,
  },
  data() {
    return {
      A: 21.5,
      Ix: 1200,
      Iy: 547,
      Ws: 15500,
      beta: 0.03,
      Lh: 32.5,  // Default horizontal distance
      Lv: 17.5,  // Default vertical distance
      xw: 17.3,  // Default value
      xr: 15.1,  // Default value
      conservativeMode: false,
      Q: 168,  // Assumed body weight (lb)
      table_5_5: [
        { harmonic: 1, fp_min: 1.6, fp_max: 2.2, alpha_v_slow: 0.5, alpha_v_normal: 0.4, alpha_h_slow: 0.1, alpha_h_normal: 0.1 },
        { harmonic: 2, fp_min: 2.8, fp_max: 3.4, alpha_v_slow: 0.2, alpha_v_normal: 0.1, alpha_h_slow: 0.1, alpha_h_normal: 0.05 },
        { harmonic: 3, fp_min: 4.4, fp_max: 5.2, alpha_v_slow: 0.1, alpha_v_normal: 0.1, alpha_h_slow: null, alpha_h_normal: null },
        { harmonic: 4, fp_min: 5.6, fp_max: 6.6, alpha_v_slow: 0.05, alpha_v_normal: 0.05, alpha_h_slow: null, alpha_h_normal: null }
      ]
    };
  },
  computed: {
    // Diagonal distance between supports (calculated from horizontal and vertical)
    Ls() {
      return Math.sqrt(Math.pow(this.Lh, 2) + Math.pow(this.Lv, 2));
    },
    // Stair angle in radians
    theta_rad() {
      return Math.atan(this.Lv / this.Lh);
    },
    // Stair angle in degrees
    theta_deg() {
      return this.theta_rad * (180 / Math.PI);
    },
    // Effective moment of inertia for vertical vibration
    Iv() {
      return 2 * this.Ix;
    },
    // Effective moment of inertia for horizontal vibration
    Ih() {
      return 2 * this.Iy;
    },
    // Vertical natural frequency (Hz)
    fv() {
      const g = 386; // in/s²
      const E = 29e6; // psi
      const Ls_inches = this.Ls * 12; // Convert ft to inches
      const numerator = g * E * this.Iv;
      const denominator = this.Ws * Math.pow(Ls_inches, 3);
      return (Math.PI / 2) * Math.sqrt(numerator / denominator);
    },
    // Horizontal natural frequency (Hz)
    fh() {
      const g = 386; // in/s²
      const E = 29e6; // psi
      const Ls_inches = this.Ls * 12; // Convert ft to inches
      const numerator = g * E * this.Ih;
      const denominator = this.Ws * Math.pow(Ls_inches, 3);
      return (Math.PI / 2) * Math.sqrt(numerator / denominator);
    },
    // Mode shape value at walker location
    phi_w() {
      if (this.conservativeMode) {
        return 1;
      }
      return Math.sin((Math.PI * this.xw) / this.Ls);
    },
    // Mode shape value at response location
    phi_r() {
      if (this.conservativeMode) {
        return 1;
      }
      return Math.sin((Math.PI * this.xr) / this.Ls);
    },
    // Check if vertical frequency is satisfactory (> 5.0 Hz)
    verticalCheck() {
      return this.fv > 5.0;
    },
    // Check if horizontal frequency is satisfactory (> 2.5 Hz)
    horizontalCheck() {
      return this.fh > 2.5;
    }
  },
  methods: {
    // Peak acceleration due to stair descent as fraction of gravity
    peakAcceleration(R, gamma) {
      const numerator = R * this.Q * Math.pow(Math.cos(this.theta_rad), 2);
      const denominator = this.beta * this.Ws;
      const term1 = 0.62 * Math.pow(Math.E, (-1 * gamma * this.fv));
      const term2 = (numerator / denominator) * this.phi_w * this.phi_r;
      const term3 = 1 - Math.pow(Math.E, -100 * this.beta);
      return term1 * term2 * term3;
    },
    // Case 1: Normal descent
    normal_descent() {
      return this.peakAcceleration(0.7, 0.29);
    },
    // Case 2: Rapidly descending individual—not perceptible
    rapid_descent_ind() {
      if (this.fv <= 8) {
        return this.peakAcceleration(0.5, 0.19);
      } else {
        return this.peakAcceleration(0.7, 0.19);
      }
    },
    // Case 3: Rapidly descending individual—perceptible; rapidly descending group
    rapid_descent_group() {
      if (this.fv <= 8) {
        return this.peakAcceleration(0.5, 0.19);
      } else {
        return this.peakAcceleration(0.7, 0.19);
      }
    }
  }
};
</script>
